import React, { createContext, useContext } from 'react';
import { BusinessUnit } from 'data/BusinessUnit';

interface BusinessUnitContextType {
  businessUnitList: BusinessUnit[];
  selectedBusinessUnit: BusinessUnit | null;
  updateBusinessUnit: (businessUnitName: string | null) => void;
  refetchBusinessUnits: () => Promise<void>;
}

export const BusinessUnitContext = createContext<BusinessUnitContextType | undefined>(undefined);

interface ProviderProps {
  children: React.ReactNode;
  businessUnitList: BusinessUnit[];
  selectedBusinessUnit: BusinessUnit | null;
  updateBusinessUnit: (businessUnitName: string | null) => void;
  refetchBusinessUnits: () => Promise<void>;
}

export const BusinessUnitProvider = ({
  children,
  businessUnitList,
  selectedBusinessUnit,
  updateBusinessUnit,
  refetchBusinessUnits
}: ProviderProps) => {
  return (
    <BusinessUnitContext.Provider value={{
      businessUnitList,
      selectedBusinessUnit,
      updateBusinessUnit,
      refetchBusinessUnits
    }}>
      {children}
    </BusinessUnitContext.Provider>
  );
};

/**
 * Custom hook to access the business unit list, selected business unit, and related functions
 * @returns {BusinessUnitContextType} The business unit list, selected business unit, and functions
 * Returns default values if used outside of BusinessUnitProvider (for backwards compatibility)
 */
export const useBusinessUnit = (): BusinessUnitContextType => {
  const context = useContext(BusinessUnitContext);
  if (context === undefined) {
    // Return default values instead of throwing error for backwards compatibility
    return {
      businessUnitList: [],
      selectedBusinessUnit: null,
      updateBusinessUnit: () => {
        console.warn('updateBusinessUnit called outside of BusinessUnitProvider');
      },
      refetchBusinessUnits: async () => {
        console.warn('refetchBusinessUnits called outside of BusinessUnitProvider');
      }
    };
  }
  return context;
};
